import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/hooks/useCart';

export default function CartPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('cart');
  const { items, removeItem, updateQuantity, emptyCart } = useCart();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);

  const isEmpty = items.length === 0;
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.productId)));
    }
  };

  const handleToggleItem = (productId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(item.productId)),
    [items, selectedIds],
  );

  const selectedTotalQuantity = useMemo(
    () => selectedItems.reduce((sum, item) => sum + item.quantity, 0),
    [selectedItems],
  );

  const handleQuantityChange = (productId: string, delta: number, currentQty: number) => {
    const next = currentQty + delta;
    if (next >= 1) {
      updateQuantity(productId, next);
    }
  };

  const handleDeleteSelected = () => {
    selectedIds.forEach((id) => removeItem(id));
    setSelectedIds(new Set());
  };

  const handleOrderClick = () => {
    if (selectedItems.length === 0) {
      setToast(t('cart.toastSelectItem'));
      setTimeout(() => setToast(null), 2500);
      return;
    }
    navigate('/get-quote');
  };

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Breadcrumb */}
      <div className="w-full bg-white border-b border-cream-200/70">
        <div className="w-full px-4 md:px-6 lg:px-10 py-3">
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <a href="/" className="hover:text-ink-700 transition-colors">{t('cart.breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-[10px]" />
            <span className="text-ink-700 font-medium">{t('cart.title')}</span>
          </div>
        </div>
      </div>

      <main className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-ink-900">{t('cart.title')}</h1>
            {!isEmpty && (
              <p className="text-sm text-ink-500 mt-1">
                {t('cart.totalCount', { count: items.length, quantity: totalQuantity.toLocaleString() })}
              </p>
            )}
          </div>
          {!isEmpty && (
            <button
              onClick={emptyCart}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink-500 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-sm" />
              {t('cart.emptyCart')}
            </button>
          )}
        </div>

        {/* Empty State */}
        {isEmpty && (
          <div className="flex flex-col items-center justify-center py-24 md:py-32">
            <div className="w-20 h-20 rounded-full bg-cream-100 flex items-center justify-center mb-5">
              <i className="ri-shopping-cart-2-line text-ink-300 text-3xl" />
            </div>
            <h2 className="text-lg font-bold text-ink-700 mb-2">{t('cart.emptyTitle')}</h2>
            <p className="text-sm text-ink-500 mb-6 text-center max-w-sm">
              {t('cart.emptyDesc')}
            </p>
            <button
              onClick={() => navigate('/packaging')}
              className="px-6 py-3 bg-warm-500 text-white text-sm font-bold rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-shopping-bag-3-line" />
              {t('cart.browseProducts')}
            </button>
          </div>
        )}

        {/* Cart Items */}
        {!isEmpty && (
          <>
            {/* Select All Bar */}
            <div className="flex items-center justify-between mb-3 px-1">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleToggleAll}
                  className="w-5 h-5 accent-warm-500 cursor-pointer rounded"
                />
                <span className="text-sm font-medium text-ink-700">
                  {t('cart.selectAll', { selected: selectedIds.size, total: items.length })}
                </span>
              </label>
              {selectedIds.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-ink-500 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line text-sm" />
                  {t('cart.deleteSelected')}
                </button>
              )}
            </div>

            {/* Items List */}
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.productId}
                  className="flex items-start gap-3 md:gap-4 p-4 border border-cream-200/70 rounded-lg bg-white hover:border-cream-300/60 transition-colors"
                >
                  {/* Checkbox */}
                  <label className="cursor-pointer select-none shrink-0 pt-0.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.productId)}
                      onChange={() => handleToggleItem(item.productId)}
                      className="w-5 h-5 accent-warm-500 cursor-pointer rounded"
                    />
                  </label>

                  {/* Product Image */}
                  <div
                    className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-cream-100 border border-cream-200/70 cursor-pointer flex items-center justify-center"
                    onClick={() => navigate(`/products/${item.productId}`)}
                  >
                    {item.productImage ? (
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <i className="ri-image-line text-ink-300 text-2xl" />
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <button
                          onClick={() => navigate(`/products/${item.productId}`)}
                          className="text-sm font-bold text-ink-700 hover:text-warm-600 transition-colors cursor-pointer text-left line-clamp-2"
                        >
                          {item.productName}
                        </button>
                        <p className="text-[11px] text-ink-500 mt-0.5">SKU: {item.sku}</p>
                        {(item.material || item.capacity) && (
                          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-ink-500 flex-wrap">
                            {item.material && (
                              <span className="flex items-center gap-1">
                                <i className="ri-flask-line text-[10px]" />
                                {item.material}
                              </span>
                            )}
                            {item.capacity && (
                              <span className="flex items-center gap-1">
                                <i className="ri-contrast-drop-2-line text-[10px]" />
                                {item.capacity}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => {
                          removeItem(item.productId);
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            next.delete(item.productId);
                            return next;
                          });
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-ink-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer shrink-0"
                        aria-label={t('cart.remove')}
                      >
                        <i className="ri-close-line text-base" />
                      </button>
                    </div>

                    {/* Unit Price & Quantity & Subtotal */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 pt-3 border-t border-cream-200/70">
                      {/* Unit Price — reference only, no client quote total */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] text-ink-500">{t('cart.unitPrice')}</span>
                        <span className="text-sm font-bold text-ink-700">{t('cart.quoteInquiry')}</span>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleQuantityChange(item.productId, -1, item.quantity)}
                          disabled={item.quantity <= 1}
                          className="w-7 h-7 flex items-center justify-center rounded-md border border-cream-300/60 text-ink-500 hover:bg-cream-100 hover:text-ink-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                        >
                          <i className="ri-subtract-line text-xs" />
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v >= 1) updateQuantity(item.productId, v);
                          }}
                          className="w-14 h-7 text-center text-xs font-bold text-ink-700 border border-cream-300/60 rounded-md focus:outline-none focus:border-warm-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          onClick={() => handleQuantityChange(item.productId, 1, item.quantity)}
                          className="w-7 h-7 flex items-center justify-center rounded-md border border-cream-300/60 text-ink-500 hover:bg-cream-100 hover:text-ink-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line text-xs" />
                        </button>
                        <span className="text-xs text-ink-500 ml-1">{item.moqUnit}</span>
                      </div>

                      {/* Subtotal — resolved by backend */}
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-ink-500">{t('cart.amount')}</span>
                        <span className="text-sm font-bold text-warm-600">{t('cart.quoteInquiry')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Total + Action Buttons */}
            <div className="mt-6 pt-5 border-t border-cream-200/70">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-ink-500 shrink-0">{t('cart.totalAmount')}</span>
                  <span className="text-lg font-bold text-warm-600 shrink-0">{t('cart.quoteInquiry')}</span>
                  {selectedItems.length > 0 && (
                    <span className="text-xs text-ink-400 shrink-0">
                      {t('cart.totalUnits', { count: selectedTotalQuantity.toLocaleString() })}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/packaging')}
                    className="px-5 py-2.5 border border-cream-300/60 text-ink-700 text-sm font-bold rounded-lg hover:bg-cream-100 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-arrow-left-line" />
                    {t('cart.continueShopping')}
                  </button>
                  <button
                    onClick={handleOrderClick}
                    className="px-6 py-2.5 bg-warm-500 text-white text-sm font-bold rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-shopping-cart-2-line" />
                    {t('cart.checkout')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-ink-700 text-white text-sm font-medium rounded-lg shadow-lg animate-fade-in-down">
          <div className="flex items-center gap-2">
            <i className="ri-information-line text-base" />
            {toast}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}